import { useEffect, type ReactNode } from 'react';
import { useMe, type Role } from './me';
import { withCategoryIntent } from './intent';
import { LOGIN_PATH, evaluateGuard, type GuardDecision } from './routing';

export interface RoleGuardProps {
  role: Role;
  children: ReactNode;
  /** Rendered while `GET /me` is in flight. A skeleton, never a bare spinner (docs/06 §4). */
  fallback?: ReactNode;
  /** Rendered on a role mismatch. In-app 403 — never reveals whether the route exists. */
  forbidden?: ReactNode;
  /** Rendered for `status = blocked`. Neutral copy: "account unavailable, contact MOST". */
  blocked?: ReactNode;
  /** Rendered for admin-created accounts still holding a temporary password. */
  mustChangePassword?: ReactNode;
}

/**
 * Wraps a whole role area. Decisions come from `GET /me` cached in TanStack Query, per docs/06 §3.
 *
 * This is UX, not authorization: the backend re-checks the role on every request. What this
 * buys is that a buyer never *sees* a producer screen flicker into existence before the API
 * refuses it.
 */
export function RoleGuard({
  role,
  children,
  fallback = null,
  forbidden = null,
  blocked = null,
  mustChangePassword = null,
}: RoleGuardProps) {
  const { data: me, isPending } = useMe();

  const decision: GuardDecision = isPending
    ? { kind: 'allow' } // provisional; the `isPending` branch below renders the fallback anyway
    : evaluateGuard(me, role);

  // /login and /verify-email live on the landing's origin — a different host since D16, so
  // the redirect is necessarily a full-page navigation, not a React Router push.
  const redirectTo =
    decision.kind === 'redirect' && decision.external ? decision.to : null;

  useEffect(() => {
    if (redirectTo && typeof window !== 'undefined') {
      // Only the login bounce carries the category slug: that is the guest arriving from a
      // landing product card, who should come back to the same category after signing in.
      window.location.assign(
        redirectTo === LOGIN_PATH ? withCategoryIntent(redirectTo, window.location.search) : redirectTo,
      );
    }
  }, [redirectTo]);

  if (isPending) return <>{fallback}</>;

  switch (decision.kind) {
    case 'redirect':
      // The effect above is navigating away; render nothing rather than a flash of content.
      return <>{fallback}</>;
    case 'blocked':
      return <>{blocked}</>;
    case 'forbidden':
      return <>{forbidden}</>;
    case 'must-change-password':
      return <>{mustChangePassword}</>;
    case 'allow':
      return <>{children}</>;
  }
}
